/**
 * Vektet salgspipeline (VIB-1304, bølge 6). Ren kalkylefil — ingen
 * Supabase/React-imports, samme stil som dashboard-bf-calc.ts / offer-closed-date.ts.
 *
 * Hele funksjonaliteten som konsumerer denne filen er bak feature-flagget
 * `feature_offer_pipeline` (src/lib/feature-flags.ts) — selve kalkylen er
 * flagg-agnostisk (ren funksjon), gatingen skjer i UI-laget.
 *
 * Vektene (system_config.offer_probability_weights) er placeholder-tall satt
 * av Erik 15.07 (0.25/0.55/0.85) — VIB-1312 kalibrerer dem senere med ekte
 * vinnrater. Denne filen tar vektene som input (Record<OfferProbability, number>),
 * aldri hardkodet — kalibrering blir en ren dataendring.
 *
 * Hjelp-tema: src/lib/hjelp/content/beregninger.ts (slug: vektet-tilbudspipeline).
 * Formel avstemt mot docs/kpi-source-of-truth.md (Erik-låst).
 */

export type OfferProbability = "low" | "likely" | "very_likely";
export type PipelineOfferStatus = "draft" | "sent" | "accepted" | "lost";

/**
 * UI-etiketter (Erik 15.07) for sannsynlighetskategoriene. Bor her (ikke i en
 * komponentfil) slik at både OfferProbabilityBadge og OfferPipelineControls
 * kan importere konstanten uten å trigge react-refresh/only-export-components
 * (ESLint krever at komponentfiler kun eksporterer komponenter).
 */
export const OFFER_PROBABILITY_LABELS: Record<OfferProbability, string> = {
  low: "Lite sannsynlig",
  likely: "Sannsynlig",
  very_likely: "Veldig sannsynlig",
};

export interface PipelineOffer {
  id: string;
  status: PipelineOfferStatus;
  probability: OfferProbability | null;
  /** ISO-dato (YYYY-MM-DD), null hvis ikke satt. */
  valid_until: string | null;
  total_amount: number;
  created_by: string;
  writers: Array<{ user_id: string; share_pct: number }>;
}

export type ProbabilityWeights = Record<OfferProbability, number>;

/** Board-bøtte et tilbud havner i. "expired" er avledet, ikke en DB-status. */
export type PipelineBucket = "draft" | "sent" | "expired" | "accepted" | "lost";

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * "Utløpt" er AVLEDET, ingen egen status/kolonne: kun sendte tilbud med
 * valid_until strengt før i dag regnes som utløpt. valid_until = i dag er
 * fortsatt gyldig (siste gyldige dag) — grensen er eksplisitt testet.
 */
export function isExpired(offer: Pick<PipelineOffer, "status" | "valid_until">, today: Date): boolean {
  if (offer.status !== "sent" || !offer.valid_until) return false;
  return offer.valid_until < toIsoDate(today);
}

/** Hvilken board-bøtte et tilbud havner i (draft/sent/expired/accepted/lost). */
export function bucketFor(offer: Pick<PipelineOffer, "status" | "valid_until">, today: Date): PipelineBucket {
  if (offer.status === "sent" && isExpired(offer, today)) return "expired";
  return offer.status;
}

/** Grupperer tilbud per board-bøtte. Rekkefølge: draft, sent, expired, accepted, lost. */
export function groupByStatus(
  offers: PipelineOffer[],
  today: Date,
): Record<PipelineBucket, PipelineOffer[]> {
  const groups: Record<PipelineBucket, PipelineOffer[]> = {
    draft: [],
    sent: [],
    expired: [],
    accepted: [],
    lost: [],
  };
  for (const o of offers) {
    groups[bucketFor(o, today)].push(o);
  }
  return groups;
}

/** Eligible for the weighted pipeline KPI: status='sent' and not expired. */
function isEligibleForPipeline(offer: PipelineOffer, today: Date): boolean {
  return offer.status === "sent" && !isExpired(offer, today);
}

export interface WeightedPipelineResult {
  /** Σ total_amount for gyldige sent-tilbud (inkl. de uten kategori). */
  rawKr: number;
  /** Σ total_amount × vekt for gyldige sent-tilbud MED kategori. */
  weightedKr: number;
  /** Antall gyldige sent-tilbud uten kategori satt (ekskludert fra weightedKr). */
  uncategorizedCount: number;
}

/**
 * Vektet tilbudspipeline (KPI-kortet + board-kolonnesum). Kun `sent`-tilbud
 * som ikke er utløpt teller. Manglende kategori (probability=null) holdes
 * UTENFOR vektet sum, men teller med i rå sum (tilbudet er reelt, bare
 * ukategorisert) — boardet viser det med "Sett sannsynlighet"-hint.
 */
export function weightedPipeline(
  offers: PipelineOffer[],
  weights: ProbabilityWeights,
  today: Date,
): WeightedPipelineResult {
  const eligible = offers.filter((o) => isEligibleForPipeline(o, today));
  let rawKr = 0;
  let weightedKr = 0;
  let uncategorizedCount = 0;
  for (const o of eligible) {
    rawKr += o.total_amount;
    if (o.probability == null) {
      uncategorizedCount += 1;
      continue;
    }
    weightedKr += o.total_amount * weights[o.probability];
  }
  return {
    rawKr: Math.round(rawKr),
    weightedKr: Math.round(weightedKr),
    uncategorizedCount,
  };
}

/**
 * Fordeler ett tilbuds beløp på selgere via `writers` (share_pct-sum), med
 * fallback til `created_by` 100 % hvis writers er tom. Samme mønster som
 * BF-attribusjon (sales_units/ownership_pct) i dashboard-bf-calc.ts.
 */
function attributeOffer(offer: PipelineOffer): Array<{ userId: string; sharePct: number }> {
  if (offer.writers.length > 0) {
    return offer.writers.map((w) => ({ userId: w.user_id, sharePct: w.share_pct }));
  }
  return [{ userId: offer.created_by, sharePct: 100 }];
}

export interface SellerPipelineResult {
  rawKr: number;
  weightedKr: number;
}

/**
 * Vektet pipeline per selger (writers-attribusjon). Kun gyldige sent-tilbud
 * med kategori bidrar til weightedKr per selger (samme kategori-eksklusjon
 * som weightedPipeline); rawKr følger tilbudets fulle andel uansett kategori.
 */
export function bySeller(
  offers: PipelineOffer[],
  weights: ProbabilityWeights,
  today: Date,
): Map<string, SellerPipelineResult> {
  const result = new Map<string, SellerPipelineResult>();
  const eligible = offers.filter((o) => isEligibleForPipeline(o, today));
  for (const o of eligible) {
    const shares = attributeOffer(o);
    for (const { userId, sharePct } of shares) {
      const fraction = sharePct / 100;
      const existing = result.get(userId) ?? { rawKr: 0, weightedKr: 0 };
      existing.rawKr += o.total_amount * fraction;
      if (o.probability != null) {
        existing.weightedKr += o.total_amount * weights[o.probability] * fraction;
      }
      result.set(userId, existing);
    }
  }
  for (const [userId, v] of result) {
    result.set(userId, { rawKr: Math.round(v.rawKr), weightedKr: Math.round(v.weightedKr) });
  }
  return result;
}
